/**
 * Config-driven qualification engine hook.
 *
 * This hook calculates qualification status using rules from the screening config API.
 * It replaces the hardcoded logic in useQualificationEngine when a config is available.
 */

import { useMemo } from 'react';

import { useGetFirmQuery } from '@/services/api/firmsService';
import {
  CaseTypeScreeningConfig,
  QualificationRulesConfig,
} from '@/services/types/screening-config-types';

import { QualificationResult, ScreeningAnswers } from '../types';

interface QualificationCriteria {
  date_of_loss_max_days?: number | null;
  accepted_states?: string[];
  case_type_actions?: Record<string, 'accept' | 'reject' | 'escalate'>;
  default_case_type_action?: 'accept' | 'reject' | 'escalate';
}

interface IntakeData {
  caseType: string;
  state: string;
  dateOfLoss: string;
}

/**
 * Calculate days since a given date.
 */
const calculateDaysSince = (dateStr: string): number => {
  if (!dateStr) return 0;
  const date = new Date(dateStr);
  const today = new Date();
  const diffTime = today.getTime() - date.getTime();
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Check if a disqualification rule matches the current answers.
 */
function matchesDisqualificationRule(
  condition: Record<string, string>,
  answers: ScreeningAnswers,
  supersedingQuestions: string[],
  checkUnlessSuperseeding: boolean,
): boolean {
  // Check if any superseding question is "yes" (which would override the disqualification)
  if (checkUnlessSuperseeding) {
    const hasSupersedingYes = supersedingQuestions.some(
      (key) => answers[key] === 'yes',
    );
    if (hasSupersedingYes) {
      return false; // Superseding condition prevents disqualification
    }
  }

  // Check if all conditions in the rule match
  return Object.entries(condition).every(([key, expectedValue]) => {
    const actualValue = answers[key];
    return actualValue === expectedValue;
  });
}

/**
 * Check if any override in a group is satisfied.
 * An override is satisfied if any question in the primary list OR
 * all questions in any alternative list have "yes" answers.
 */
function isOverrideGroupSatisfied(
  overrideGroup: { primary: string[]; alternatives: string[][] },
  answers: ScreeningAnswers,
): boolean {
  // Check primary overrides (any one is sufficient)
  const hasPrimaryOverride = overrideGroup.primary.some(
    (key) => answers[key] === 'yes',
  );
  if (hasPrimaryOverride) return true;

  // Check alternative groups (all questions in any group must be yes)
  return overrideGroup.alternatives.some((altGroup) =>
    altGroup.every((key) => answers[key] === 'yes'),
  );
}

/**
 * Get the effective answer for a category, considering overrides.
 * Returns 'yes' if the main question is yes, or if unknown with valid override.
 */
function getEffectiveAnswerFromConfig(
  mainQuestionKey: string,
  answers: ScreeningAnswers,
  rules: QualificationRulesConfig,
): 'yes' | 'no' | 'unknown' {
  const answer = answers[mainQuestionKey];

  if (answer === 'yes') return 'yes';
  if (answer === 'no') return 'no';

  // For unknown, check if any override group applies
  // Overrides can come from this category's override group OR other categories
  const hasOverride = Object.values(rules.override_groups).some(
    (overrideGroup) => isOverrideGroupSatisfied(overrideGroup, answers),
  );

  if (hasOverride) {
    return 'yes'; // Override unknown to yes
  }

  return 'unknown';
}

/**
 * Get parent questions from the config categories.
 */
function getParentQuestions(
  config: CaseTypeScreeningConfig,
): Array<{ key: string; label: string; categoryType: string }> {
  return config.categories.map((category) => ({
    key: category.main_question.key,
    label: category.letter, // Use letter as short label
    categoryType: category.category_type,
  }));
}

/**
 * Calculate qualification status using config-driven rules.
 */
export function calculateQualificationFromConfig(
  answers: ScreeningAnswers,
  firmCriteria: QualificationCriteria | null,
  intakeData: IntakeData,
  config: CaseTypeScreeningConfig,
): QualificationResult {
  const { rules } = config;
  const escalationReasons: string[] = [];

  // =========================================
  // FIRM CRITERIA CHECKS
  // =========================================

  // Case type rejected by firm → Disqualify (hard stop)
  if (firmCriteria && intakeData.caseType) {
    const action =
      firmCriteria.case_type_actions?.[intakeData.caseType] ??
      firmCriteria.default_case_type_action;
    if (action === 'reject') {
      return {
        status: 'disqualified',
        reasons: [`Firm doesn't accept ${intakeData.caseType} cases`],
      };
    }
    if (action === 'escalate') {
      escalationReasons.push(
        `Case type ${intakeData.caseType} requires review`,
      );
    }
  }

  // Date of loss exceeds limit → Escalate
  if (firmCriteria?.date_of_loss_max_days && intakeData.dateOfLoss) {
    const daysSince = calculateDaysSince(intakeData.dateOfLoss);
    if (daysSince > firmCriteria.date_of_loss_max_days) {
      escalationReasons.push(
        `Date of loss exceeds ${firmCriteria.date_of_loss_max_days} day limit`,
      );
    }
  }

  // State not accepted → Escalate
  if (
    firmCriteria?.accepted_states?.length &&
    intakeData.state &&
    !firmCriteria.accepted_states.includes(intakeData.state)
  ) {
    escalationReasons.push(`State ${intakeData.state} not accepted by firm`);
  }

  // =========================================
  // CONFIG-DRIVEN DISQUALIFICATION RULES
  // =========================================

  const matchingDisqualificationRule = rules.disqualification.find((rule) =>
    matchesDisqualificationRule(
      rule.condition,
      answers,
      rules.superseding_questions,
      rule.unless_superseding,
    ),
  );

  if (matchingDisqualificationRule) {
    return {
      status: 'disqualified',
      reasons: [matchingDisqualificationRule.reason],
    };
  }

  // =========================================
  // CONFIG-DRIVEN ESCALATION TRIGGERS
  // =========================================

  // Check if any escalation trigger is active
  // Triggers fire when the answer matches the configured value (e.g., 'apartment_complex_/_hoa' for dropdown)
  rules.escalation_triggers.forEach((trigger) => {
    const answer = answers[trigger.key];
    if (answer === trigger.value) {
      escalationReasons.push(trigger.label);
    }
  });

  // =========================================
  // CHECK PARENT CATEGORY ANSWERS
  // =========================================

  const parentQuestions = getParentQuestions(config);

  parentQuestions.forEach(({ key, label }) => {
    const effectiveAnswer = getEffectiveAnswerFromConfig(key, answers, rules);

    if (effectiveAnswer === 'no') {
      escalationReasons.push(`${label}: No`);
    } else if (effectiveAnswer === 'unknown') {
      escalationReasons.push(`${label}: Unknown (no override)`);
    }
  });

  // If any escalation reasons, escalate
  if (escalationReasons.length > 0) {
    return { status: 'escalate', reasons: escalationReasons };
  }

  // =========================================
  // CHECK IF ALL REQUIREMENTS MET (QUALIFIED)
  // =========================================

  const allParentsYes = parentQuestions.every(({ key }) => {
    const effectiveAnswer = getEffectiveAnswerFromConfig(key, answers, rules);
    return effectiveAnswer === 'yes';
  });

  if (allParentsYes) {
    return { status: 'qualified', reasons: ['All requirements met'] };
  }

  // =========================================
  // PENDING (Incomplete answers)
  // =========================================

  const pendingQuestions = parentQuestions
    .filter(({ key }) => {
      const answer = answers[key];
      return !answer || answer === 'unknown';
    })
    .map(({ label }) => label);

  return {
    status: 'pending',
    reasons:
      pendingQuestions.length > 0
        ? [`Awaiting: ${pendingQuestions.join(', ')}`]
        : ['Incomplete answers'],
  };
}

/**
 * Hook to calculate qualification status using config-driven rules.
 *
 * This is a drop-in replacement for useQualificationEngine that uses
 * rules from the screening config API instead of hardcoded constants.
 *
 * @param firmId - The firm ID for firm-specific criteria
 * @param answers - Current screening answers
 * @param caseType - The case type
 * @param state - The state abbreviation
 * @param dateOfLoss - The date of loss
 * @param config - The screening config from the API (optional)
 * @returns Qualification result with status and reasons
 */
export function useConfigDrivenQualification(
  firmId: string | undefined,
  answers: ScreeningAnswers,
  caseType: string,
  state: string,
  dateOfLoss: string,
  config: CaseTypeScreeningConfig | undefined,
): QualificationResult {
  const { data: firm } = useGetFirmQuery(firmId ?? '', {
    skip: !firmId,
  });

  const qualificationCriteria = useMemo((): QualificationCriteria | null => {
    if (!firm?.intake_preferences?.qualification_criteria) {
      return null;
    }
    return firm.intake_preferences.qualification_criteria;
  }, [firm]);

  const result = useMemo(() => {
    // If no config, return pending (fallback to legacy engine)
    if (!config) {
      return {
        status: 'pending' as const,
        reasons: ['Loading configuration...'],
      };
    }

    const intakeData: IntakeData = { caseType, state, dateOfLoss };
    return calculateQualificationFromConfig(
      answers,
      qualificationCriteria,
      intakeData,
      config,
    );
  }, [answers, qualificationCriteria, caseType, state, dateOfLoss, config]);

  return result;
}

export type { IntakeData, QualificationCriteria };
